import { Head, Link } from "@inertiajs/react"
import { PublicShell } from "@/components/PublicShell"
import { Heart, Zap, EyeOff, Database } from "lucide-react"
import { Button } from "@/components/ui/button"

type Donor = {
  name:       string
  tier:       "annual" | "lifetime"
  donated_at: string
}

type Contributor = {
  name:          string
  id:            number
  contributions: number
}

type Props = {
  donors:       Donor[]
  contributors: Contributor[]
}

const TIER_LABEL: Record<Donor["tier"], string> = {
  annual:   "Annual supporter",
  lifetime: "Lifetime supporter",
}

export default function WallOfFame({ donors, contributors }: Props) {
  const title = "Wall of Fame — Australian Music Database"
  const description = "The Australian Music Database is free, independent, and run by music fans. Donations keep it that way."

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
      </Head>
      <PublicShell>
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-16">

          {/* ── Mission ─────────────────────────────────────────────── */}
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center shrink-0">
              <Heart className="w-5 h-5 text-amber-600 dark:text-amber-400 fill-current" />
            </div>
            <h1 className="mb-0">Wall of Fame</h1>
          </div>

          <p className="text-lg text-stone-600 dark:text-stone-400 mb-4 leading-relaxed">
            The Australian Music Database is free, independent, and run by music fans —
            no investors, no paywalls, no agenda beyond getting the history right.
          </p>
          <p className="text-stone-500 dark:text-stone-500 mb-8 leading-relaxed">
            Keeping it online costs money. A small one-time donation removes ads from your
            experience and gets your name on this page (if you want it there).
          </p>

          <Link href="/donate">
            <Button size="lg" className="mb-12">
              <Heart className="w-4 h-4 mr-2 fill-current" />
              Support the database
            </Button>
          </Link>

          {/* ── What you get ────────────────────────────────────────── */}
          <div className="rounded-xl border border-hairline bg-surface p-6 mb-12">
            <h2 className="text-base mb-4">What your support does</h2>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <EyeOff className="w-4 h-4 mt-0.5 text-amber-500 shrink-0" />
                <span className="text-sm text-ink-body">
                  <strong>Removes ads</strong> across the entire site — for one year or forever, depending on your tier.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <Zap className="w-4 h-4 mt-0.5 text-amber-500 shrink-0" />
                <span className="text-sm text-ink-body">
                  <strong>Keeps the lights on</strong> — hosting, development, and the ongoing research it takes to keep records accurate.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <Heart className="w-4 h-4 mt-0.5 text-amber-500 shrink-0 fill-current" />
                <span className="text-sm text-ink-body">
                  <strong>Gets you listed here</strong> — a small but permanent thank-you, if you'd like it.
                </span>
              </li>
            </ul>
          </div>

          {/* ── Donors ──────────────────────────────────────────────── */}
          {donors.length > 0 && (
            <div className="mb-12">
              <div className="flex items-center gap-2 mb-1">
                <Heart className="w-4 h-4 text-amber-500 fill-current" />
                <h2 className="text-base mb-0">Financial supporters</h2>
              </div>
              <p className="text-sm text-stone-500 dark:text-stone-400 mb-6">
                {donors.length === 1
                  ? "One person has donated so far. Thank you."
                  : `${donors.length} people have donated. Thank you.`}
              </p>
              <ul className="divide-y divide-hairline">
                {donors.map((donor, i) => (
                  <li key={i} className="py-3.5 flex items-center justify-between gap-4">
                    <div>
                      <span className="font-medium text-ink-body">{donor.name}</span>
                      <span className="ml-2 text-xs text-stone-400 dark:text-stone-500">
                        {TIER_LABEL[donor.tier]}
                      </span>
                    </div>
                    <time
                      className="text-sm text-stone-400 dark:text-stone-500 shrink-0"
                      dateTime={donor.donated_at}
                    >
                      {new Date(donor.donated_at).toLocaleDateString("en-AU", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })}
                    </time>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* ── Contributors ────────────────────────────────────────── */}
          {contributors.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Database className="w-4 h-4 text-amber-500" />
                <h2 className="text-base mb-0">Database contributors</h2>
              </div>
              <p className="text-sm text-stone-500 dark:text-stone-400 mb-6">
                These people have added or edited records in the database. The whole thing
                wouldn't exist without them.
              </p>
              <ul className="divide-y divide-hairline">
                {contributors.map((c) => (
                  <li key={c.id} className="py-3 flex items-center justify-between gap-4">
                    <span className="font-medium text-ink-body">{c.name}</span>
                    <span className="text-sm text-stone-400 dark:text-stone-500 shrink-0 tabular-nums">
                      {c.contributions.toLocaleString()} records
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

        </div>
      </PublicShell>
    </>
  )
}
